import { createContext } from "react";

import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    useLoggedInHasJiraAndNotConfluence,
    useLoggedOut
} from "@/components/user-context";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import GlobalNav from "@/patterns/global-nav/src";
import { ExperimentView } from "@/utils/experiment";
import { experiments } from "@/utils/experiment/manifest";
import { FeatureExposed } from "@/utils/feature-exposed";
import { ParentScrollTracking } from "@/utils/scroll-tracking";

import StickyContainer from "../../../../patterns/sticky-container/sticky-container";

import {
    FutureOfTeamwork,
    AtlassianIntelligence,
    CustomerStories,
    GetStartedCTA,
    HeroTakeoverTeam25,
    HeroJiraCtaTemplate,
    ScrollPinnedSections,
    TabsAndIntegrationsSections,
    HomePageBannerAlert,
    TeamStats,
    ConfluenceCrossflowCTA,
    LoggedInHeroContainer,
    BelowNavBannerAlert
} from "./sections-v2";
import { HeroTWCHeader } from "./sections-v2/hero-twc-header";
import { HeroTeamworkCollection } from "./sections-v2/hero-twc-tabs";
import { JiraLoggedInHeader } from "./sections-v2/jira-logged-in-header";
import { JPDCrossflowComponent } from "./sections-v2/jpd-xflow-hero/homepage-jpd-xflow";
import { ContentTypesSection } from "./sections-v2/jsm-xflow-hero/content-types-section";
import { JSMCrossflowComponent } from "./sections-v2/jsm-xflow-hero/homepage-jsm-xflow";
import { LoomCrossflowComponent } from "./sections-v2/loom-xflow-hero/homepage-loom-xflow";
import { ProductProvider } from "./sections-v2/reusable-xflow-hero-component/product-context";

export const HeroSelectedTeamIndexContext = createContext<number | undefined>(
    undefined
);

export const HomePageTemplate = () => {
    const isLoggedIn = !useLoggedOut();
    const userHasJiraAndNotConfluence = useLoggedInHasJiraAndNotConfluence();

    return (
        <UFOSegment name="home-page-v2">
            <div>
                <StickyContainer>
                    <HomePageBannerAlert />
                    <GlobalNav productKey={"home"} />
                    <BelowNavBannerAlert />
                </StickyContainer>
                <TypographyStyles>
                    <main id="content">
                        <ParentScrollTracking
                            scrollTrackingEnabled={true}
                            pageId="home-page-v2"
                        >
                            <HeroTakeoverTeam25 />
                            {isLoggedIn ? (
                                <LoggedInHeroContainer>
                                    {userHasJiraAndNotConfluence ? (
                                        <FeatureExposed
                                            experimentKey="gwp-5831-homepage-confluence-xflow"
                                            group="Test"
                                        >
                                            <ProductProvider product="confluence">
                                                <ConfluenceCrossflowCTA
                                                    contentComponent={
                                                        ContentTypesSection
                                                    }
                                                />
                                            </ProductProvider>
                                        </FeatureExposed>
                                    ) : (
                                        <ExperimentView
                                            experiment={
                                                experiments.gwp7037HomepageJsmXflow
                                            }
                                            render={(value) => {
                                                if (value) {
                                                    return (
                                                        <ProductProvider product="jsm">
                                                            <JSMCrossflowComponent
                                                                contentComponent={
                                                                    ContentTypesSection
                                                                }
                                                            />
                                                        </ProductProvider>
                                                    );
                                                }
                                                return (
                                                    <HeroJiraCtaTemplate>
                                                        <JiraLoggedInHeader />
                                                    </HeroJiraCtaTemplate>
                                                );
                                            }}
                                            notEnrolled={
                                                <ExperimentView
                                                    experiment={
                                                        experiments.gwp7038HomepageJpdXflow
                                                    }
                                                    render={(value) => {
                                                        if (value) {
                                                            return (
                                                                <ProductProvider product="jpd">
                                                                    <JPDCrossflowComponent
                                                                        contentComponent={
                                                                            ContentTypesSection
                                                                        }
                                                                    />
                                                                </ProductProvider>
                                                            );
                                                        }
                                                        return (
                                                            <HeroJiraCtaTemplate>
                                                                <JiraLoggedInHeader />
                                                            </HeroJiraCtaTemplate>
                                                        );
                                                    }}
                                                    notEnrolled={
                                                        <ExperimentView
                                                            experiment={
                                                                experiments.gwp7473HomepageLoomXflow
                                                            }
                                                            render={(value) => {
                                                                if (value) {
                                                                    return (
                                                                        <ProductProvider product="loom">
                                                                            <LoomCrossflowComponent
                                                                                contentComponent={
                                                                                    ContentTypesSection
                                                                                }
                                                                            />
                                                                        </ProductProvider>
                                                                    );
                                                                }
                                                                return (
                                                                    <HeroJiraCtaTemplate>
                                                                        <JiraLoggedInHeader />
                                                                    </HeroJiraCtaTemplate>
                                                                );
                                                            }}
                                                            notEnrolled={
                                                                <HeroJiraCtaTemplate>
                                                                    <JiraLoggedInHeader />
                                                                </HeroJiraCtaTemplate>
                                                            }
                                                        />
                                                    }
                                                />
                                            }
                                        />
                                    )}
                                </LoggedInHeroContainer>
                            ) : (
                                // user not logged-in
                                <ExperimentView
                                    experiment={
                                        experiments.gwp8655WacHomepageTWCHero
                                    }
                                    render={(value) => {
                                        if (value === "test") {
                                            return (
                                                <HeroTeamworkCollection>
                                                    <HeroTWCHeader />
                                                </HeroTeamworkCollection>
                                            );
                                        }
                                        return (
                                            <HeroJiraCtaTemplate>
                                                <JiraLoggedInHeader />
                                            </HeroJiraCtaTemplate>
                                        );
                                    }}
                                    notEnrolled={
                                        <HeroJiraCtaTemplate>
                                            <JiraLoggedInHeader />
                                        </HeroJiraCtaTemplate>
                                    }
                                />
                            )}
                            <ScrollPinnedSections />
                            <AtlassianIntelligence />
                            <TeamStats />
                            <TabsAndIntegrationsSections />
                            <CustomerStories />
                            <FutureOfTeamwork />
                            <GetStartedCTA />
                        </ParentScrollTracking>
                    </main>
                </TypographyStyles>
                <GlobalFooter />
            </div>
        </UFOSegment>
    );
};
